import { useTranslation } from 'react-i18next'
import {
  languageOptions,
  scriptStyleOptions,
  targetPlatformOptions,
  videoAspectRatioOptions,
  videoLengthOptions,
  visualStyleOptions
} from './options'

// Helper function to get valid option values
const getValidOptionValues = (options) => options.map(option => option.value)

// Text field validation
const validateTextField = (fieldName, value, t, { min = 1, max = 1000 } = {}) => {
  if (!value || value.trim() === '') {
    return t(`${fieldName} is required`)
  }
  if (value.trim().length < min) {
    return t(`${fieldName} should be at least ${min} characters long`)
  }
  if (value.trim().length > max) {
    return t(`${fieldName} should not exceed ${max} characters`)
  }
  return null
}

// Option field validation
const validateOptionField = (fieldName, value, validOptions, t) => {
  if (!value) {
    return t(`${fieldName} is required`)
  }
  if (!validOptions.includes(value)) {
    return t(`Please select a valid ${fieldName.toLowerCase()}`)
  }
  return null
}

// Main form validation
export const validateVideoCreationForm = (values, t) => {
  let isFormValid = true
  const errors = {}

  const validators = {
    name: () => validateTextField('Name of the video', values.name, t, { min: 3, max: 100 }),
    language: () => validateOptionField('Language', values.language, getValidOptionValues(languageOptions), t),
    target_platform: () => validateOptionField('Target platform', values.target_platform, getValidOptionValues(targetPlatformOptions), t),
    target_audience: () => validateTextField('Target audience', values.target_audience, t),
    aspect_ratio: () => validateOptionField('Aspect ratio', values.aspect_ratio, getValidOptionValues(videoAspectRatioOptions), t),
    video_length: () => validateOptionField('Video length', values.video_length, getValidOptionValues(videoLengthOptions), t),
    script_style: () => validateOptionField('Script style', values.script_style, getValidOptionValues(scriptStyleOptions), t),
    visual_style: () => validateOptionField('Visual style', values.visual_style, getValidOptionValues(visualStyleOptions), t),
    override_script: () => validateTextField('AI description', values.override_script, t, { min: 10, max: 1000 })
  }

  for (const key in validators) {
    const error = validators[key]()
    if (error) {
      errors[key] = error
      isFormValid = false
    }
  }

  return { isFormValid, errors }
}

// Ant Design validation rules generator
const createTextFieldRules = (label, t, min = 1, max = 1000) => [
  { required: true, message: t(`${label} is required`) },
  { min, message: t(`${label} should be at least ${min} characters long`) },
  { max, message: t(`${label} should not exceed ${max} characters`) },
  { whitespace: true, message: t(`${label} cannot be empty`) }
]

export const useVideoValidationRules = () => {
  const { t } = useTranslation()

  const validationRules = {
    name: createTextFieldRules('Name of the video', t, 3, 100),
    language: [{ required: true, message: t('Language is required') }],
    target_platform: [{ required: true, message: t('Target platform is required') }],
    target_audience: createTextFieldRules('Target audience', t),
    aspect_ratio: [{ required: true, message: t('Aspect ratio is required') }],
    video_length: [{ required: true, message: t('Video length is required') }],
    script_style: [{ required: true, message: t('Script style is required') }],
    visual_style: [{ required: true, message: t('Visual style is required') }],
    override_script: createTextFieldRules('AI description', t, 10, 1000)
  }

  return {
    validationRules,
    validateForm: (values) => validateVideoCreationForm(values, t)
  }
}
